import { ChangeDetectionStrategy, Component, computed, Input, signal } from "@angular/core";
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from "@angular/forms";
import { DemoAssetViewModel, SuggestedBehavior, SuggestedDemo } from "../../core/model/ContentManagement";

@Component({
  selector: "brd-suggested-editor",
  templateUrl: "./suggested-editor.component.html",
  styleUrl: "./suggested-editor.component.less",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SuggestedEditorComponent,
      multi: true
    }
  ]
})
export class SuggestedEditorComponent implements ControlValueAccessor {
  @Input() assets!: DemoAssetViewModel[];

  mappedSuggestedDemos = computed(() => {
    return this.suggestedDemos().map(suggestedDemo => {
      const asset = this.assets.find(x => x.id === suggestedDemo.suggestedId);
      return {
        ...suggestedDemo,
        asset: asset
          ? `${asset.name} (RAM ${this.formatConsumption(asset.ramConsumption)} - Disk ${this.formatConsumption(asset.diskConsumption)})`
          : ""
      };
    });
  });
  filteredAssets = computed(() => {
    return this.assets
      .filter(asset => {
        return !this.suggestedDemos().some(suggestedDemo => suggestedDemo.suggestedId === asset.id);
      })
      .map(asset => ({
        id: asset.id,
        displayValue: `${asset.name} (RAM ${this.formatConsumption(asset.ramConsumption)} - Disk ${this.formatConsumption(asset.diskConsumption)})`
      }));
  });
  private suggestedDemos = signal<SuggestedDemo[]>([]);

  private suggestedDemoFormControl = new FormControl<number | null>(null);
  newSuggestedFormGroup = new FormGroup({
    demoId: this.suggestedDemoFormControl,
    behavior: new FormControl<SuggestedBehavior>(SuggestedBehavior.suggested)
  });

  onChange: (value: SuggestedDemo[]) => void = () => {};
  onTouched: () => void = () => {};
  isDisabled = false;

  suggestedBehaviors = [
    { value: SuggestedBehavior.suggested, label: "Suggested" },
    { value: SuggestedBehavior.default, label: "Default" }
  ];

  constructor() {
    this.suggestedDemoFormControl.valueChanges.subscribe(value => {
      if (value) {
        const asset = this.assets.find(x => x.id === value);
        const newSuggestedDemo: SuggestedDemo = {
          suggestedId: value,
          behavior: this.newSuggestedFormGroup.value.behavior ?? SuggestedBehavior.suggested,
          suggestedRamConsumption: asset?.ramConsumption ?? 0,
          suggestedDiskConsumption: asset?.diskConsumption ?? 0
        };
        this.suggestedDemoFormControl.setValue(null);
        this.updateSuggestedDemos("ADD", newSuggestedDemo);
      }
    });
  }

  writeValue(obj: SuggestedDemo[]): void {
    this.suggestedDemos.set(obj || []);
  }
  registerOnChange(fn: (value: SuggestedDemo[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  deleteSuggestedDemo(index: number): void {
    this.updateSuggestedDemos("DELETE", undefined, index);
  }

  updateSuggestedDemo(value: SuggestedBehavior, i: number): void {
    const suggestedDemo = this.suggestedDemos()[i];
    this.updateSuggestedDemos(
      "UPDATE",
      {
        ...suggestedDemo,
        behavior: value
      },
      i
    );
  }

  private formatConsumption(value: number): string {
    if (value >= 1000) {
      return `${parseFloat((value / 1000).toFixed(2))} GB`;
    } else {
      return `${value} MB`;
    }
  }

  private updateSuggestedDemos(action: "ADD" | "UPDATE" | "DELETE", value?: SuggestedDemo, index?: number): void {
    this.suggestedDemos.update(demos => {
      const newValues = [...demos];
      switch (action) {
        case "ADD":
          newValues.push(value!);
          break;
        case "UPDATE":
          newValues[index!] = value!;
          break;
        case "DELETE":
          newValues.splice(index!, 1);
          break;
      }

      this.onChange(newValues);
      return newValues;
    });
  }
}
